import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TimesheetProvider } from './contexts/TimesheetContext';
import { PersonaProvider } from './contexts/PersonaContext';
import { DataSourceProvider } from './contexts/DataSourceContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { SettingsProvider } from './contexts/SettingsContext';
import './utils/clearCache'; // Import to make clearTimesheetCache available globally
import { TimesheetGrid } from './components/TimesheetGrid';
import { ApprovalDashboard } from './components/ApprovalDashboard';
import { SidebarProvider } from './components/blocks/evo-sidebar';
import { LeftMenu } from './components/LeftMenu';
import { Header } from './components/Navigation/Header';
import { PersonaDashboard } from './components/dashboard/PersonaDashboard';
import { PlaceholderPage } from './components/Pages/PlaceholderPage';
import { AlertsDemoPage } from './components/Pages/AlertsDemoPage';
import { TimesheetReportsPage } from './components/Pages/TimesheetReportsPage';
import { MissingTimesheetsReportPage } from './components/Pages/MissingTimesheetsReportPage';
import { UtilisationReportPage } from './components/Pages/UtilisationReportPage';
import { SettingsPage } from './components/Pages/SettingsPage';
import { NotificationsPage } from './components/Pages/NotificationsPage';
import { ScreenSettingsPage } from './components/Pages/ScreenSettingsPage';
import { RolesAndRatesPage } from './components/Pages/RolesAndRatesPage';
import { RolesAndRatesPageSimple } from './components/Pages/RolesAndRatesPageSimple';
import { RolesPermissionsPage } from './components/Pages/RolesPermissionsPage';
import { EditRolePage } from './components/Pages/EditRolePage';
import { ActiveProjectsPage } from './components/Pages/ActiveProjectsPage';
import { TeamMembersPage } from './components/Pages/TeamMembersPage';
import { DocumentationPage } from './components/Pages/DocumentationPage';
import { FontWeightTest } from './components/FontWeightTest';
import { PageViewTracker } from './components/PageViewTracker';

import { CompaniesPage } from './components/Pages/CompaniesPage';
import { CompanyProvider } from '@/features/multi-company';

import { ApprovalTestPage } from './components/Pages/ApprovalTestPage';

import { Toaster } from '@/components/ui/sonner';
import { WelcomeModal } from '@/components/ProductTour';
import { BootstrapGate } from '@/components/BootstrapGate';
import {
  BarChart3,
  Users,
  Briefcase,
  Zap,
  Download,
  Settings,
  HelpCircle,
  TrendingUp,
} from 'lucide-react';
import type { TimesheetStatus } from './types/timesheet';

// Navigation state types
type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

type ReportFiltersState = {
  statuses?: TimesheetStatus[];
  employeeIds?: number[];
  dateFrom?: Date;
  dateTo?: Date;
  quickDateRange?: string;
};

type AppNavigationState = {
  timesheetDrilldown?: TimesheetDrilldownState;
  reportFilters?: ReportFiltersState;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navigationState, setNavigationState] = useState<AppNavigationState | null>(null);

  const currentPath = location.pathname;

  const handleNavigate = (path: string, state?: AppNavigationState) => {
    setNavigationState(state ?? null);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <LeftMenu activePage={currentPath} onPageChange={handleNavigate} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header currentPath={currentPath} onNavigate={handleNavigate} />
          
          {/* Main content with top padding to account for sticky header */}
          <main className="flex-1 overflow-auto pt-3 px-3 pb-3 bg-neutral-50">
            <div className="w-full max-w-[2000px] mx-auto h-full">
            <Routes>
              {/* Homepage */}
              <Route path="/" element={<PersonaDashboard onNavigate={handleNavigate} />} />
              
              {/* Timesheet */}
              <Route path="/timesheet" element={<TimesheetGrid selection={navigationState?.timesheetDrilldown} />} />
              
              {/* Approvals */}
              <Route path="/approvals" element={<ApprovalDashboard />} />
              
              {/* Reports */}
              <Route path="/reports/timesheet" element={<TimesheetReportsPage onNavigate={handleNavigate} initialFilters={navigationState?.reportFilters} />} />
              <Route path="/reports/missing-timesheets" element={<MissingTimesheetsReportPage />} />
              <Route path="/reports/utilisation" element={<UtilisationReportPage />} />
              <Route
                path="/reports/billable"
                element={
                  <PlaceholderPage
                    title="Billable Hours"
                    description="Billable vs non-billable analysis"
                    icon={BarChart3}
                  />
                }
              />
              
              {/* Analytics */}
              <Route
                path="/analytics"
                element={
                  <PlaceholderPage
                    title="Analytics"
                    description="Advanced analytics and insights"
                    icon={TrendingUp}
                  />
                }
              />
              
              {/* Team */}
              <Route path="/team/members" element={<TeamMembersPage />} />
              <Route
                path="/team/schedule"
                element={
                  <PlaceholderPage
                    title="Team Schedule"
                    description="View team availability and schedules"
                    icon={Users}
                  />
                }
              />
              <Route
                path="/team/capacity"
                element={
                  <PlaceholderPage
                    title="Capacity Planning"
                    description="Resource capacity planning and forecasting"
                    icon={TrendingUp}
                  />
                }
              />
              
              {/* Projects */}
              <Route path="/projects/active" element={<ActiveProjectsPage />} />
              <Route
                path="/projects/archive"
                element={
                  <PlaceholderPage
                    title="Archived Projects"
                    description="View completed and archived projects"
                    icon={Briefcase}
                  />
                }
              />
              <Route
                path="/projects/budget"
                element={
                  <PlaceholderPage
                    title="Budget Tracking"
                    description="Project budget and forecasting"
                    icon={Briefcase}
                  />
                }
              />
              
              {/* Companies & Integrations */}
              <Route path="/integrations" element={<CompaniesPage />} />
              {/* Legacy integration routes - redirect to main companies page */}
              <Route path="/integrations/erp" element={<Navigate to="/integrations" replace />} />
              <Route path="/integrations/hr" element={<Navigate to="/integrations" replace />} />
              <Route path="/integrations/sync" element={<Navigate to="/integrations" replace />} />
              
              {/* Data */}
              <Route
                path="/data/import"
                element={
                  <PlaceholderPage
                    title="Import Data"
                    description="Import timesheet data from external sources"
                    icon={Download}
                  />
                }
              />
              <Route
                path="/data/export"
                element={
                  <PlaceholderPage
                    title="Export Data"
                    description="Export data to CSV, Excel, or PDF"
                    icon={Download}
                  />
                }
              />
              
              {/* Settings */}
              <Route path="/settings/general" element={<SettingsPage onNavigate={handleNavigate} />} />
              <Route path="/settings/roles-permissions" element={<RolesPermissionsPage onNavigate={handleNavigate} />} />
              <Route path="/settings/roles-permissions/create" element={<EditRolePage onNavigate={handleNavigate} />} />
              <Route path="/settings/roles-permissions/edit/:roleId" element={<EditRolePage onNavigate={handleNavigate} />} />
              <Route path="/settings/screens" element={<ScreenSettingsPage />} />
              <Route path="/settings/roles" element={<RolesAndRatesPage onNavigate={handleNavigate} />} />
              <Route path="/settings/approvals" element={<ApprovalTestPage />} />
              <Route
                path="/settings/notifications"
                element={<NotificationsPage onNavigate={handleNavigate} />}
              />
              <Route
                path="/settings/security"
                element={
                  <PlaceholderPage
                    title="Security"
                    description="Security settings and permissions"
                    icon={Settings}
                  />
                }
              />
              
              {/* Help */}
              <Route path="/help/docs" element={<DocumentationPage onNavigate={handleNavigate} />} />
              <Route
                path="/help/support"
                element={
                  <PlaceholderPage
                    title="Contact Support"
                    description="Get help from our support team"
                    icon={HelpCircle}
                  />
                }
              />
              
              {/* Font Test Page */}
              <Route path="/font-test" element={<FontWeightTest />} />
              
              {/* Demo Pages */}
              <Route path="/demo/alerts" element={<AlertsDemoPage />} />
              
              {/* Fallback route */}
              <Route path="*" element={<PersonaDashboard onNavigate={handleNavigate} />} />
            </Routes>
            </div>
          </main>
        </div>
      </div>

      {/* Welcome Modal for first-time users */}
      <WelcomeModal tourId="welcome-tour" autoShow={true} />

      <Toaster />
    </SidebarProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <DataSourceProvider>
        <SettingsProvider>
          <CompanyProvider>
            <TimesheetProvider>
              <PermissionProvider>
                <PersonaProvider isManager={true}>
                  <BootstrapGate>
                    <AppContent />
                  </BootstrapGate>
                </PersonaProvider>
              </PermissionProvider>
            </TimesheetProvider>
          </CompanyProvider>
        </SettingsProvider>
      </DataSourceProvider>
    </BrowserRouter>
  );
}
